import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { useTimeline } from '../contexts/TimelineContext';
import { doc, getDoc, collection, query, where, orderBy, onSnapshot, updateDoc, getDocs, limit, deleteDoc } from 'firebase/firestore';
import PageHeader from '../components/PageHeader';
// MilestoneModal removed - handled by specific tabs now
import DynamicTimeline from '../components/DynamicTimeline';
import LiveChatPanel from '../components/LiveChatPanel';
import IncidentDetailsTabs from '../components/IncidentDetailsTabs';
import SmartAutomationAlerts from '../components/SmartAutomationAlerts';
import AutomationStatusBadge from '../components/AutomationStatusBadge';
import Phase2AutomationPanel from '../components/Phase2AutomationPanel';

import { Timestamp } from 'firebase/firestore';
import { Form, Input, Select, DatePicker, Button, Space, Card, Row, Col, Typography, Badge, Modal, message, Collapse, Tooltip, Dropdown } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined, InfoCircleOutlined, DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { logActivity, ACTIVITY_TYPES, createIncidentLog } from '../utils/activityLogger';
import videoMeetingMonitorService from '../services/videoMeetingMonitorService';
import '../styles/IncidentDetails.css';
import { INCIDENT_TYPES } from '../constants/incidentTypes';
import VISXTimeline from '../components/timeline/VISXTimeline';
import TimeInput from '../components/TimeInput';
import { getUserDisplayName } from '../utils/userDisplayUtils';
import { updateRegulatoryDeadlineTimes } from '../utils/regulatoryDeadlines';
import moment from 'moment';
import { exportIncidentSiteStatus, exportIncidentStaffStatus } from '../utils/incidentExportUtils';
import GeographySelectionModal from '../components/GeographySelectionModal';
import geocodingService from '../services/geocodingService';
import LocationInput from '../components/LocationInput';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const INCIDENT_STATUS = ['Active', 'Stabilising', 'Resolved', 'Exercise'];
const SEVERITY_LEVELS = ['Low', 'Moderate', 'High', 'Extreme'];

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'not started':
      return '#6c757d';
    case 'in progress':
      return '#0d6efd';
    case 'complete':
      return '#198754';
    case 'blocked':
      return '#dc3545';
    default:
      return '#6c757d';
  }
};

const formatCrisisType = (incident) => {
  // If it's "Other" and has a custom type, show the custom type
  if (incident?.crisisType === 'Other' && incident?.customCrisisType) {
    return incident.customCrisisType;
  }
  
  // Otherwise, format the regular crisis type
  const type = incident?.crisisType || 'Unknown';
  return type.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

const INCIDENT_ACTIVITY_TYPES = {
  ...ACTIVITY_TYPES,
  INCIDENT_CREATED: 'incident_created',
  INCIDENT_DELETED: 'incident_deleted',
  INCIDENT_STATUS_CHANGED: 'incident_status_changed',
  INCIDENT_SEVERITY_CHANGED: 'incident_severity_changed',
  INCIDENT_LOCATION_CHANGED: 'incident_location_changed',
  INCIDENT_OCCURRENCE_TIME_CHANGED: 'incident_occurrence_time_changed',
  INCIDENT_ASSIGNED: 'incident_assigned',
  INCIDENT_UPDATED: 'incident_updated'
};

const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

// Helper function to get user display name with CMT role
const getUserDisplayNameWithRole = (user) => {
  const displayName = getUserDisplayName(user);
  
  // Add CMT role if available
  if (user.cmtRole && user.cmtRole.trim()) {
    // Keep CMT fully capitalized
    const roleDisplay = user.cmtRole.replace(/\bcmt\b/gi, 'CMT');
    return `${displayName} (${roleDisplay})`;
  }
  
  // Fallback to system role if no CMT role
  if (user.role && user.role !== 'cmt_member') {
    let roleDisplay = user.role.replace(/_/g, ' ').split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    // Keep CMT fully capitalized in system roles too
    roleDisplay = roleDisplay.replace(/\bcmt\b/gi, 'CMT');
    return `${displayName} (${roleDisplay})`;
  }
  
  return displayName;
};

// Helper function to get CMT Leader for placeholder text
const getCMTLeaderPlaceholder = (users) => {
  if (!users || users.length === 0) return "No CMT Members available";
  
  // Look for CMT Leader in cmtRole field first
  const cmtLeader = users.find(user => 
    user.cmtRole && (
      user.cmtRole.toLowerCase().includes('leader') || 
      user.cmtRole.toLowerCase().includes('cmt leader')
    )
  );
  
  if (cmtLeader) {
    return getUserDisplayNameWithRole(cmtLeader);
  }
  
  // Fallback: look for cmt_leader in system role
  const systemCmtLeader = users.find(user => 
    user.role && user.role.toLowerCase().includes('cmt_leader')
  );
  
  if (systemCmtLeader) {
    return getUserDisplayNameWithRole(systemCmtLeader);
  }
  
  // Final fallback
  return "No CMT Leader found";
};

const IncidentHeader = ({ incident, onUpdate, users, onDelete }) => {
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [detailsExpanded, setDetailsExpanded] = useState(false); // Default to collapsed to save space
  const [geographyModalVisible, setGeographyModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const { currentUser } = useAuth();
  const { db } = useFirestore();

  const canDelete = currentUser?.role === 'developer' || 
                   currentUser?.role === 'cmt_leader' || 
                   currentUser?.role === 'cmt_admin' ||
                   currentUser?.isAdmin;

  // EXPANDED PERMISSIONS: Allow ANY CMT member to edit incident status (especially to "Resolved")
  // This includes cmt_member, cmt_leader, cmt_admin, crisis_manager, safety_officer, comms_media, legal_compliance
  const canEdit = currentUser?.role === 'developer' || 
                 currentUser?.role === 'cmt_leader' || 
                 currentUser?.role === 'cmt_admin' ||
                 currentUser?.role === 'cmt_member' ||
                 currentUser?.role === 'crisis_manager' ||
                 currentUser?.role === 'safety_officer' ||
                 currentUser?.role === 'comms_media' ||
                 currentUser?.role === 'legal_compliance' ||
                 currentUser?.isAdmin ||
                 // Also check if user is part of incident's CMT members
                 (incident?.cmtMemberIds && Array.isArray(incident.cmtMemberIds) && incident.cmtMemberIds.includes(currentUser?.uid));

  useEffect(() => {
    // 🔧 CRITICAL DEBUGGING: Track all form reset attempts
    console.log('🔧 FORM RESET EFFECT TRIGGERED:', {
      hasIncident: !!incident,
      hasForm: !!form,
      editing: editing,
      shouldReset: !!(incident && form && !editing),
      incidentId: incident?.id,
      incidentStatus: incident?.status
    });

    if (incident && form && !editing) {
      console.log('🔧 PROCEEDING WITH FORM RESET - User not editing');
      
      // Convert occurredAt timestamp to separate date and time for editing
      let occurredDate = null;
      let occurredTime = null;
      
      if (incident.occurredAt) {
        const occurredMoment = moment(incident.occurredAt.toDate());
        occurredDate = occurredMoment.format('YYYY-MM-DD'); // HTML date input format
        occurredTime = occurredMoment.format('HH:mm'); // Convert to string for text input
      }

      // Set form values with capitalization for status and severity
      const formValues = {
        title: incident.title,
        crisisType: incident.crisisType,
        customCrisisType: incident.customCrisisType || null,
        status: incident.status ? capitalize(incident.status) : '',
        severity: incident.severity ? capitalize(incident.severity) : '',
        location: incident.location,
        summary: incident.summary || '',
        leadUserId: incident.leadUserId || null,  // Ensure it's explicitly set
        cmtMemberIds: incident.cmtMemberIds || [],  // Ensure it's an array
        occurredDate: occurredDate,
        occurredTime: occurredTime
      };

      // Force form to update with new values
      form.setFieldsValue(formValues);
      
      console.log('✅ FORM RESET COMPLETED:', {
        status: formValues.status,
        severity: formValues.severity,
        title: formValues.title,
        editing: editing
      });
    } else {
      console.log('🚫 FORM RESET BLOCKED:', {
        reason: !incident ? 'No incident' : !form ? 'No form' : editing ? 'Currently editing' : 'Unknown',
        editing: editing
      });
    }
  }, [incident, form, editing]);

  // Listen for timeline updates to sync form fields
  useEffect(() => {
    const handleTimelineUpdate = (event) => {
      const { incidentId, occurredAt, newTime } = event.detail;
      
      if (incidentId === incident?.id && form) {
        const occurredMoment = moment(newTime);
        const occurredDate = occurredMoment.format('YYYY-MM-DD');
        const occurredTime = occurredMoment.format('HH:mm');
        
        // Update form fields
        form.setFieldsValue({
          occurredDate: occurredDate,
          occurredTime: occurredTime
        });
        
        console.log('📅 Form updated from timeline drag:', {
          occurredDate,
          occurredTime,
          newTime: occurredMoment.format('DD/MM/YYYY HH:mm')
        });
      }
    };

    window.addEventListener('incidentTimelineUpdate', handleTimelineUpdate);
    
    return () => {
      window.removeEventListener('incidentTimelineUpdate', handleTimelineUpdate);
    };
  }, [incident?.id, form]);

  // Debug logging for users
  useEffect(() => {
    console.log('DEBUG - Users state updated:', {
      count: users?.length || 0,
      users: users?.map(u => ({
        uid: u.uid,
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName,
        role: u.role
      })) || []
    });
  }, [users]);

  // State to force re-render when "Set to Today" is clicked
  const [dateInputKey, setDateInputKey] = useState(0);

  // Quick Fix: Use useCallback to prevent function recreation on every render
  const handleSetToday = useCallback(() => {
    const todayFormatted = moment().format('YYYY-MM-DD');
    form.setFieldValue('occurredDate', todayFormatted);
    // Force the input to re-render immediately by changing its key
    setDateInputKey(prev => prev + 1);
  }, [form]);

  // NUCLEAR OPTION: Use simple HTML date input instead of Ant Design DatePicker
  const handleSimpleDateChange = useCallback((e) => {
    const dateValue = e.target.value; // This will be in YYYY-MM-DD format
    form.setFieldsValue({ occurredDate: dateValue }); // Use setFieldsValue instead
    form.validateFields(); // Validate all fields to force update
  }, [form]);

  // Convert moment/date to YYYY-MM-DD format for HTML input
  const getSimpleDateValue = useCallback(() => {
    const formValue = form.getFieldValue('occurredDate');
    if (!formValue) return '';
    
    // If it's already a string in YYYY-MM-DD format, return it
    if (typeof formValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(formValue)) {
      return formValue;
    }
    
    // If it's a moment object or date, convert it
    return moment(formValue).format('YYYY-MM-DD');
  }, [form]);

  const handleSave = async (values) => {
    try {
      console.log('DEBUG - handleSave called with:', {
        values,
        incident: incident ? {
          id: incident.id,
          status: incident.status,
          severity: incident.severity,
          location: incident.location
        } : null,
        onUpdate: typeof onUpdate
      });
      // Calculate occurredAt timestamp from date and time
      let occurredAt = null;
      console.log('🚨 DEBUG - Date/Time Processing:', {
        hasOccurredDate: !!values.occurredDate,
        hasOccurredTime: !!values.occurredTime,
        occurredDate: values.occurredDate,
        occurredTime: values.occurredTime,
        occurredDateRaw: values.occurredDate ? values.occurredDate.toString() : null,
        occurredDateFormatted: values.occurredDate ? moment(values.occurredDate).format('DD/MM/YYYY') : null,
        existingOccurredAt: incident?.occurredAt ? moment(incident.occurredAt.toDate()).format('DD/MM/YYYY HH:mm') : 'Not set',
        allFormValues: values
      });

      if (values.occurredDate || values.occurredTime) {
        // Use existing incident data to fill in missing pieces
        let targetDate = values.occurredDate;
        let targetTime = values.occurredTime;
        
        // If date is missing but time is provided, use existing date
        if (!targetDate && incident?.occurredAt) {
          targetDate = moment(incident.occurredAt.toDate());
          console.log('📅 Using existing date:', targetDate.format('DD/MM/YYYY'));
        }
        
        // If time is missing but date is provided, use existing time or default to 00:00
        if (!targetTime) {
          if (incident?.occurredAt) {
            targetTime = moment(incident.occurredAt.toDate()).format('HH:mm');
            console.log('⏰ Using existing time:', targetTime);
          } else {
            targetTime = '00:00';
            console.log('⏰ Using default time: 00:00');
          }
        }

        if (targetDate && targetTime) {
          // Parse the time string (HH:mm format)
          const timeParts = targetTime.split(':');
          const hours = parseInt(timeParts[0], 10);
          const minutes = parseInt(timeParts[1], 10);
          
          // Ensure targetDate is a proper moment object
          let dateMoment;
          if (moment.isMoment(targetDate)) {
            dateMoment = targetDate.clone(); // Clone to avoid mutation
          } else {
            // Fix timezone issue: Extract date components from the Date object
            // and create a new moment in local timezone
            const dateObj = new Date(targetDate);
            dateMoment = moment()
              .year(dateObj.getFullYear())
              .month(dateObj.getMonth()) // getMonth() returns 0-based month
              .date(dateObj.getDate());
            
            console.log('🔧 Date parsing fix:', {
              originalDate: targetDate,
              originalDateString: targetDate.toString(),
              extractedYear: dateObj.getFullYear(),
              extractedMonth: dateObj.getMonth(),
              extractedDate: dateObj.getDate(),
              resultingMoment: dateMoment.format('DD/MM/YYYY')
            });
          }
          
          const occurredMoment = dateMoment
            .hour(hours)
            .minute(minutes)
            .second(0)
            .millisecond(0);
          occurredAt = Timestamp.fromDate(occurredMoment.toDate());
          
          console.log('✅ Combined Date/Time Result:', {
            originalTargetDate: targetDate,
            targetDateFormatted: dateMoment.format('DD/MM/YYYY'),
            targetTime: targetTime,
            combined: occurredMoment.format('DD/MM/YYYY HH:mm:ss'),
            timestamp: occurredAt,
            isMoment: moment.isMoment(targetDate)
          });
        }
      } else if (incident?.occurredAt) {
        // If both fields are empty but we had existing data, preserve it
        occurredAt = incident.occurredAt;
        console.log('🔄 Preserving existing occurredAt:', moment(occurredAt.toDate()).format('DD/MM/YYYY HH:mm'));
      }

      // Always capitalize status and severity before saving
      const updatedValues = {
        ...values,
        title: values.title, // Preserve original capitalization
        crisisType: values.crisisType, // Preserve original capitalization
        customCrisisType: values.crisisType === 'Other' ? values.customCrisisType : null, // Only save custom type when "Other" is selected
        status: values.status ? capitalize(values.status) : '',
        severity: values.severity ? capitalize(values.severity) : '',
        location: values.location, // Preserve original capitalization
        occurredAt: occurredAt, // When the incident actually occurred
        updatedAt: Timestamp.now()
      };

      // Remove the separate date/time fields from the update (they're just for editing)
      delete updatedValues.occurredDate;
      delete updatedValues.occurredTime;

      // Clean up undefined values - Firestore doesn't accept undefined
      // Convert undefined to null for optional fields, or remove them entirely
      Object.keys(updatedValues).forEach(key => {
        if (updatedValues[key] === undefined) {
          // For leadUserId, preserve the existing value if new value is undefined
          if (key === 'leadUserId' && incident?.leadUserId) {
            updatedValues[key] = incident.leadUserId;
          }
          // For optional fields that can be null, set to null instead of deleting
          else if (['cmtMemberIds', 'customCrisisType'].includes(key)) {
            updatedValues[key] = null;
          } else {
            // For other fields, remove undefined values
            delete updatedValues[key];
          }
        }
        // Also handle empty arrays for cmtMemberIds
        if (key === 'cmtMemberIds' && Array.isArray(updatedValues[key]) && updatedValues[key].length === 0) {
          updatedValues[key] = null;
        }
        // Ensure leadUserId is never accidentally set to null if it has a value
        if (key === 'leadUserId' && !updatedValues[key] && incident?.leadUserId) {
          updatedValues[key] = incident.leadUserId;
        }
      });

      console.log('DEBUG - Cleaned updatedValues:', updatedValues);

      // Helper function to normalize values for comparison (prevents false positives)
      const normalizeValue = (value) => {
        if (typeof value === 'string') {
          return value.toLowerCase().trim();
        }
        return value;
      };

      // Log changes with CMT information (only if incident exists and there's a real change)
      
      // Log title changes
      if (incident && normalizeValue(values.title) !== normalizeValue(incident.title)) {
        await createIncidentLog({
          incidentId: incident.id,
          orgId: currentUser.orgId,
          action: INCIDENT_ACTIVITY_TYPES.INCIDENT_UPDATED,
          details: `Incident title changed from "${incident.title}" to "${values.title}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: incident.createdByName || currentUser.email,
          priority: 'normal',
          metadata: {
            oldValue: incident.title,
            newValue: values.title,
            changeType: 'title'
          }
        });
      }

      // Log crisis type changes
      if (incident && normalizeValue(values.crisisType) !== normalizeValue(incident.crisisType)) {
        await createIncidentLog({
          incidentId: incident.id,
          orgId: currentUser.orgId,
          action: INCIDENT_ACTIVITY_TYPES.INCIDENT_UPDATED,
          details: `Crisis type changed from "${incident.crisisType}" to "${values.crisisType}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: incident.createdByName || currentUser.email,
          priority: 'high',
          metadata: {
            oldValue: incident.crisisType,
            newValue: values.crisisType,
            changeType: 'crisis_type'
          }
        });
      }

      // Log custom crisis type changes (when crisis type is "Other")
      if (incident && values.crisisType === 'Other' && normalizeValue(values.customCrisisType) !== normalizeValue(incident.customCrisisType)) {
        const oldCustomType = incident.customCrisisType || 'Not specified';
        const newCustomType = values.customCrisisType || 'Not specified';
        
        await createIncidentLog({
          incidentId: incident.id,
          orgId: currentUser.orgId,
          action: INCIDENT_ACTIVITY_TYPES.INCIDENT_UPDATED,
          details: `Custom crisis type changed from "${oldCustomType}" to "${newCustomType}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: incident.createdByName || currentUser.email,
          priority: 'high',
          metadata: {
            oldValue: incident.customCrisisType,
            newValue: values.customCrisisType,
            changeType: 'custom_crisis_type'
          }
        });
      }

      // Log summary changes
      if (incident && normalizeValue(values.summary || '') !== normalizeValue(incident.summary || '')) {
        const oldSummary = incident.summary || 'No summary';
        const newSummary = values.summary || 'No summary';
        
        await createIncidentLog({
          incidentId: incident.id,
          orgId: currentUser.orgId,
          action: INCIDENT_ACTIVITY_TYPES.INCIDENT_UPDATED,
          details: `Incident summary updated`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: incident.createdByName || currentUser.email,
          priority: 'normal',
          metadata: {
            oldValue: oldSummary,
            newValue: newSummary,
            changeType: 'summary',
            oldSummaryLength: oldSummary.length,
            newSummaryLength: newSummary.length
          }
        });
      }
      
      if (incident && normalizeValue(values.status) !== normalizeValue(incident.status)) {
        await createIncidentLog({
          incidentId: incident.id,
          orgId: currentUser.orgId,
          action: INCIDENT_ACTIVITY_TYPES.INCIDENT_STATUS_CHANGED,
          details: `Status changed from "${incident.status}" to "${values.status}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: incident.createdByName || currentUser.email,
          priority: 'high',
          metadata: {
            oldValue: incident.status,
            newValue: values.status,
            changeType: 'status'
          }
        });
      }

      if (incident && normalizeValue(values.severity) !== normalizeValue(incident.severity)) {
        await createIncidentLog({
          incidentId: incident.id,
          orgId: currentUser.orgId,
          action: INCIDENT_ACTIVITY_TYPES.INCIDENT_SEVERITY_CHANGED,
          details: `Severity changed from "${incident.severity}" to "${values.severity}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: incident.createdByName || currentUser.email,
          priority: values.severity === 'Extreme' ? 'critical' : 'high',
          metadata: {
            oldValue: incident.severity,
            newValue: values.severity,
            changeType: 'severity'
          }
        });
      }

      if (incident && normalizeValue(values.location) !== normalizeValue(incident.location)) {
        await createIncidentLog({
          incidentId: incident.id,
          orgId: currentUser.orgId,
          action: INCIDENT_ACTIVITY_TYPES.INCIDENT_LOCATION_CHANGED,
          details: `Location changed from "${incident.location}" to "${values.location}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: incident.createdByName || currentUser.email,
          priority: 'normal',
          metadata: {
            oldValue: incident.location,
            newValue: values.location,
            changeType: 'location'
          }
        });
      }

      // Log incident lead assignment changes (only if incident exists and there's a real change)
      console.log('🔍 ABOUT TO CHECK LEAD ASSIGNMENT CHANGES');
      if (incident && values.leadUserId !== incident.leadUserId) {
        console.log('🔍 LEAD ASSIGNMENT CHANGED - Starting lead change logging');
        // Get user names for display
        const oldLeadUser = users?.find(u => u.uid === incident.leadUserId);
        const newLeadUser = users?.find(u => u.uid === values.leadUserId);
        const oldLeadName = oldLeadUser ? getUserDisplayName(oldLeadUser) : (incident.leadUserId ? 'Unknown User' : 'Unassigned');
        const newLeadName = newLeadUser ? getUserDisplayName(newLeadUser) : (values.leadUserId ? 'Unknown User' : 'Unassigned');
        
        console.log('🔍 ABOUT TO CREATE LEAD ASSIGNMENT LOG');
        await createIncidentLog({
          incidentId: incident.id,
          orgId: currentUser.orgId,
          action: INCIDENT_ACTIVITY_TYPES.INCIDENT_ASSIGNED,
          details: `Incident lead changed from "${oldLeadName}" to "${newLeadName}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: incident.createdByName || currentUser.email,
          priority: 'high',
          metadata: {
            oldLeadUserId: incident.leadUserId || null,
            newLeadUserId: values.leadUserId || null,
            oldLeadName: oldLeadName,
            newLeadName: newLeadName,
            changeType: 'lead_assignment'
          }
        });
        console.log('🔍 LEAD ASSIGNMENT LOG COMPLETED');
      }
      console.log('🔍 FINISHED LEAD ASSIGNMENT CHECK');

      console.log('🔍 FINISHED LOCATION ACTIVITY LOGGING - Moving to occurrence time logging');

      // Log occurrence date/time changes (only if incident exists)
      console.log('🔍 ABOUT TO CHECK OCCURRENCE TIME CHANGES');
      if (incident) {
        console.log('🔍 CHECKING OCCURRENCE TIME CHANGES');
        const oldOccurredAt = incident.occurredAt ? moment(incident.occurredAt.toDate()).format('DD/MM/YYYY HH:mm') : 'Not set';
        const newOccurredAt = occurredAt ? moment(occurredAt.toDate()).format('DD/MM/YYYY HH:mm') : 'Not set';
        
        console.log('🔍 OCCURRENCE TIME COMPARISON:', { oldOccurredAt, newOccurredAt, changed: oldOccurredAt !== newOccurredAt });
        
        if (oldOccurredAt !== newOccurredAt) {
          console.log('🔍 OCCURRENCE TIME CHANGED - Creating occurrence time log');
          await createIncidentLog({
            incidentId: incident.id,
            orgId: currentUser.orgId,
            action: INCIDENT_ACTIVITY_TYPES.INCIDENT_OCCURRENCE_TIME_CHANGED,
            details: `Incident occurrence time changed from "${oldOccurredAt}" to "${newOccurredAt}"`,
            userId: currentUser.uid,
            userEmail: currentUser.email,
            userFullName: incident.createdByName || currentUser.email,
            priority: 'normal',
            metadata: {
              oldValue: oldOccurredAt,
              newValue: newOccurredAt,
              changeType: 'occurrence_time'
            }
          });
          
          // CRITICAL: Update all regulatory deadline times when occurrence time changes
          // This ensures regulatory deadlines automatically update regardless of whether 
          // the change was made via timeline drag or form field input
          console.log('🔍 ABOUT TO START REGULATORY DEADLINE UPDATE');
          try {
            console.log('🔄 FORM UPDATE: Updating regulatory deadlines due to occurrence time change');
            
            // We need to get the regulatory rules and organization data
            // Since this is in the IncidentHeader component, we need to fetch this data
            const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
            const orgDoc = await getDoc(orgRef);
            
            if (orgDoc.exists() && occurredAt) {
              const orgData = orgDoc.data();
              const regulatoryRules = orgData.regulatoryRules || [];
              
              if (regulatoryRules.length > 0) {
                // Update regulatory deadlines
                await updateRegulatoryDeadlineTimes(
                  incident.id,
                  currentUser.orgId,
                  occurredAt.toDate(), // Convert Timestamp to Date
                  regulatoryRules,
                  currentUser.uid,
                  db
                );
                console.log('✅ FORM UPDATE: All regulatory deadline times updated successfully');
                
                // ADDED: Also update regulatory notification task due dates
                const { updateRegulatoryTaskTimes } = await import('../utils/regulatoryDeadlines');
                await updateRegulatoryTaskTimes(
                  incident.id,
                  currentUser.orgId,
                  occurredAt.toDate(), // Convert Timestamp to Date
                  regulatoryRules,
                  currentUser.uid,
                  db
                );
                console.log('✅ FORM UPDATE: All regulatory notification task times updated successfully');
                
                message.success('Incident occurrence time, regulatory deadlines, and notification tasks updated');
              } else {
                console.log('ℹ️ FORM UPDATE: No regulatory rules found - no deadlines to update');
              }
            }
          } catch (regulatoryError) {
            console.error('❌ FORM UPDATE: Failed to update regulatory deadline times:', regulatoryError);
            message.warning('Incident occurrence time updated but failed to update regulatory deadlines');
          }
          console.log('🔍 FINISHED REGULATORY DEADLINE UPDATE SECTION');
        }
      }
      console.log('🔍 FINISHED OCCURRENCE TIME CHECK');

      console.log('🔍 FINISHED ALL ACTIVITY LOGGING - About to start geocoding checks');
      console.log('🔍 REACHED GEOCODING SECTION - About to start geocoding logic');

      // 🗺️ AUTOMATIC GEOCODING: Check if location changed and needs geocoding
      const locationChanged = incident && values.location && values.location.trim() !== incident.location?.trim();
      const needsGeocoding = geocodingService.needsGeocoding({ 
        location: values.location, 
        latitude: incident?.latitude, 
        longitude: incident?.longitude 
      });

      console.log('🗺️ GEOCODING DEBUG:', {
        locationChanged,
        needsGeocoding,
        hasLocation: !!values.location,
        locationLength: values.location?.trim()?.length || 0,
        condition1: locationChanged || needsGeocoding,
        condition2: values.location && values.location.trim().length > 3,
        finalCondition: (locationChanged || needsGeocoding) && values.location && values.location.trim().length > 3,
        oldLocation: incident?.location?.trim(),
        newLocation: values.location?.trim(),
        incidentLatLng: { lat: incident?.latitude, lng: incident?.longitude }
      });

      if ((locationChanged || needsGeocoding) && values.location && values.location.trim().length > 3) {
        console.log('🗺️ Location changed or needs geocoding, attempting automatic geocoding:', values.location);
        try {
          const geocodingResult = await geocodingService.geocodeCrisisLocation(values.location);
          
          if (geocodingResult.success) {
            console.log('✅ Crisis location geocoded successfully during edit:', geocodingResult);
            
            // Add geocoding results to the update
            updatedValues.latitude = geocodingResult.latitude;
            updatedValues.longitude = geocodingResult.longitude;
            updatedValues.formattedLocation = geocodingResult.formattedAddress;
            updatedValues.geocodedAt = Timestamp.now();
            updatedValues.geocodingSource = geocodingResult.source;

            // 🔄 PHASE 1 ENHANCEMENT: Store reverse geocoded name if available
            if (geocodingResult.reverseGeocodeName) {
              updatedValues.reverseGeocodeName = geocodingResult.reverseGeocodeName;
            }
            
            // Show success message to user
            message.success(`Location "${values.location}" mapped successfully`);
            
            // REMOVED: Geocoding activity logging (was showing persistent annoying messages)
            // if (incident?.id) {
            //   await createIncidentLog({
            //     incidentId: incident.id,
            //     orgId: currentUser.orgId,
            //     action: INCIDENT_ACTIVITY_TYPES.INCIDENT_LOCATION_CHANGED,
            //     details: `Crisis location geocoded: "${values.location}" mapped to coordinates ${geocodingResult.latitude}, ${geocodingResult.longitude}`,
            //     userId: currentUser.uid,
            //     userEmail: currentUser.email,
            //     userFullName: incident.createdByName || currentUser.email,
            //     priority: 'normal',
            //     metadata: {
            //       location: values.location,
            //       formattedAddress: geocodingResult.formattedAddress,
            //       latitude: geocodingResult.latitude,
            //       longitude: geocodingResult.longitude,
            //       geocodingSource: geocodingResult.source,
            //       changeType: 'location_geocoded'
            //     }
            //   });
            // }
          } else {
            console.warn('⚠️ Crisis location geocoding failed during edit:', geocodingResult.error);
            // Don't block incident update on geocoding failure
            message.warning(`Location "${values.location}" could not be mapped automatically. Coordinates can be updated later.`);
          }
        } catch (geocodingError) {
          console.error('❌ Crisis geocoding error during edit:', geocodingError);
          // Don't block incident update on geocoding failure
          message.warning('Location geocoding failed. You can update coordinates later.');
        }
      }

      if (onUpdate) {
        await onUpdate(updatedValues);
      }
      console.log('🔧 EDITING MODE: Turning OFF editing mode');
      setEditing(false);
    } catch (error) {
      console.error('Error updating incident:', error);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== 'DELETE') {
      message.error('Please type "DELETE" to confirm');
      return;
    }

    try {
      await onDelete();
      setDeleteModalVisible(false);
      setDeleteConfirmation('');
    } catch (error) {
      console.error('Error deleting incident:', error);
      message.error('Failed to delete incident');
    }
  };

  // Handle site status export with geography selection
  const handleSiteExport = () => {
    setGeographyModalVisible(true);
  };

  // Handle staff status export (direct export)
  const handleStaffExport = async () => {
    try {
      setExportLoading(true);
      const filename = await exportIncidentStaffStatus(
        db, 
        incident.id, 
        currentUser.orgId, 
        incident.title
      );
      message.success(`Staff status exported successfully as ${filename}`);
    } catch (error) {
      console.error('Error exporting staff status:', error);
      message.error('Failed to export staff status');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle geography selection confirmation
  const handleGeographyConfirm = async (selectedRegions) => {
    try {
      setExportLoading(true);
      setGeographyModalVisible(false);
      
      const filename = await exportIncidentSiteStatus(
        currentUser.orgId,
        incident.title,
        selectedRegions
      );
      
      const regionText = selectedRegions.length > 0 
        ? ` for ${selectedRegions.length} selected region(s)`
        : ' for all regions';
      
      message.success(`Site status exported successfully as ${filename}${regionText}`);
    } catch (error) {
      console.error('Error exporting site status:', error);
      message.error('Failed to export site status');
    } finally {
      setExportLoading(false);
    }
  };

  // Export dropdown menu items
  const exportMenuItems = [
    {
      key: 'site-status',
      label: (
        <Space>
          <DownloadOutlined />
          Export Site Status (.csv)
        </Space>
      ),
      onClick: handleSiteExport
    },
    {
      key: 'staff-status', 
      label: (
        <Space>
          <DownloadOutlined />
          Export Staff Status (.csv)
        </Space>
      ),
      onClick: handleStaffExport
    }
  ];

  return (
    <Card 
      style={{ 
        marginBottom: '4px',
        padding: '0',
        height: 'auto',
        overflow: 'visible'
      }}
      styles={{
        body: {
          padding: '0',
          margin: '0',
          height: 'auto',
          overflow: 'visible'
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={incident}
        key={incident?.id || 'incident-form'}
        style={{ marginBottom: 0 }}
      >
        {/* Always Visible: Title and Actions */}
        <Row gutter={[16, 2]} style={{ padding: '12px' }}>
          <Col span={24}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Title level={3} style={{ margin: 0 }}>
                {editing ? (
                  <Form.Item name="title" style={{ margin: 0 }}>
                    <Input placeholder="Incident Title" />
                  </Form.Item>
                ) : (
                  incident?.title
                )}
              </Title>
              <Space>
                {!editing && false && (
                  <AutomationStatusBadge 
                    incidentId={incident?.id}
                    incidentData={incident}
                    showLabel={true}
                    size="small"
                  />
                )}
                {editing ? (
                  <>
                    <Tooltip title="Save all changes to the incident details">
                      <Button 
                        type="primary"
                        icon={<SaveOutlined />} 
                        onClick={() => form.submit()}
                        style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
                      >
                        Save
                      </Button>
                    </Tooltip>
                    <Tooltip title="Cancel editing and discard any unsaved changes">
                      <Button icon={<CloseOutlined />} onClick={() => {
                        console.log('🔧 EDITING MODE: User clicked Cancel - turning OFF editing mode');
                        setEditing(false);
                      }}>
                        Cancel
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip title="Export live CSV snapshots of site and staff status for this crisis">
                      <Dropdown 
                        menu={{ items: exportMenuItems }}
                        placement="bottomRight"
                        trigger={['click']}
                      >
                        <Button 
                          icon={<DownloadOutlined />}
                          loading={exportLoading}
                          style={{ 
                            backgroundColor: '#39325b',
                            borderColor: '#39325b',
                            color: 'white'
                          }}
                        >
                          Export ▼
                        </Button>
                      </Dropdown>
                    </Tooltip>
                    {canEdit && (
                      <Tooltip title="Edit incident details including status, severity, location, and summary">
                        <Button 
                          icon={<EditOutlined />} 
                          onClick={() => {
                            console.log('🔧 EDITING MODE: User clicked Edit - turning ON editing mode');
                            setEditing(true);
                          }}
                          style={{ 
                            backgroundColor: '#39325b',
                            borderColor: '#39325b',
                            color: 'white'
                          }}
                        >
                          Edit
                        </Button>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Delete this incident permanently - this action cannot be undone">
                        <Button 
                          danger 
                          icon={<DeleteOutlined />} 
                          onClick={() => setDeleteModalVisible(true)}
                        >
                          Delete Incident
                        </Button>
                      </Tooltip>
                    )}
                  </>
                )}
              </Space>
            </Space>
          </Col>
        </Row>
        
        {/* Collapsible Details Section */}
        <Collapse
          activeKey={detailsExpanded ? ['details'] : []}
          onChange={(keys) => setDetailsExpanded(keys.includes('details'))}
          ghost
          style={{ marginTop: '0' }}
        >
          <Panel
            header={
              <Space>
                <InfoCircleOutlined style={{ fontSize: '16px', color: '#39325b' }} />
                <strong style={{ fontSize: '15px' }}>📋 Crisis Details</strong>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {incident?.crisisType ? formatCrisisType(incident) : 'No type set'} • {incident?.status ? capitalize(incident.status) : 'No status'} • {incident?.severity ? capitalize(incident.severity) : 'No severity'}
                </Text>
              </Space>
            }
            key="details"
          >
            <Row gutter={[16, 2]} style={{ padding: '0 12px' }}>
          <Col span={8}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Crisis Type</span>}
              name="crisisType"
              rules={[{ required: true, message: 'Please select crisis type' }]}
              style={{ marginBottom: '4px' }}
            >
              <Select 
                disabled={!editing || !canEdit}
                placeholder="Select Crisis Type"
                onChange={(value) => {
                  // Clear custom crisis type when changing from "Other" to something else
                  if (value !== 'Other') {
                    form.setFieldValue('customCrisisType', null);
                  }
                }}
              >
                {INCIDENT_TYPES.map(type => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
            </Form.Item>
            
            {/* Custom Crisis Type field - only show when "Other" is selected */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.crisisType !== currentValues.crisisType
              }
            >
              {({ getFieldValue }) => {
                return getFieldValue('crisisType') === 'Other' ? (
                  <Form.Item
                    label={<span style={{ fontWeight: 'bold' }}>Specify Crisis Type</span>}
                    name="customCrisisType"
                    rules={[{ required: true, message: 'Please specify the crisis type' }]}
                    style={{ marginBottom: '4px', marginTop: '8px' }}
                  >
                    <Input 
                      disabled={!editing || !canEdit}
                      placeholder="Please specify the crisis type"
                    />
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Status</span>}
              name="status"
              rules={[{ required: true, message: 'Please select status' }]}
              style={{ marginBottom: '4px' }}
            >
              <Select 
                disabled={!editing}
                placeholder="Select Status"
                optionLabelProp="label"
              >
                {INCIDENT_STATUS.map(status => (
                  <Option key={status} value={status} label={capitalize(status)}>
                    <Badge status={getStatusBadgeClass(status)} text={capitalize(status)} />
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Severity</span>}
              name="severity"
              rules={[{ required: true, message: 'Please select severity' }]}
              style={{ marginBottom: '4px' }}
            >
              <Select 
                disabled={!editing}
                placeholder="Select Severity"
                optionLabelProp="label"
              >
                {SEVERITY_LEVELS.map(severity => (
                  <Option key={severity} value={severity} label={capitalize(severity)}>
                    <Badge status={getSeverityBadgeClass(severity)} text={capitalize(severity)} />
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Location</span>}
              name="location"
              rules={[{ required: true, message: 'Please enter location' }]}
              style={{ marginBottom: '4px' }}
            >
              <LocationInput 
                disabled={!editing}
                placeholder="Enter Location"
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Incident Lead</span>}
              name="leadUserId"
              style={{ marginBottom: '4px' }}
            >
              <Select 
                disabled={!editing}
                placeholder="Select Incident Lead"
                allowClear
              >
                {users?.map(user => (
                  <Option key={user.uid} value={user.uid}>
                    {getUserDisplayName(user)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* View CMT Members - Always visible, view-only dropdown */}
          <Col span={8}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>View CMT Members</span>}
              style={{ marginBottom: '4px' }}
            >
              <Select 
                placeholder="View CMT Members"
                disabled={false}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                style={{ width: '100%' }}
              >
                {users?.filter(user => 
                  user.role === 'cmt_leader' || 
                  user.role === 'cmt_admin' || 
                  user.role === 'cmt_member' ||
                  user.role === 'crisis_manager' ||
                  user.role === 'safety_officer' ||
                  user.role === 'comms_media' ||
                  user.role === 'legal_compliance' ||
                  user.cmtRole // Include users with CMT roles from organization data
                ).map(user => (
                  <Option key={user.uid || user.id} value={user.uid || user.id}>
                    {getUserDisplayNameWithRole(user)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Date and Time Fields - New row */}
          <Col span={8}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Date Incident Occurred</span>}
              name="occurredDate"
              style={{ marginBottom: '4px' }}
            >
              <div>
                <input
                  type="date"
                  className="form-control"
                  disabled={!editing}
                  defaultValue={getSimpleDateValue()}
                  onChange={handleSimpleDateChange}
                  style={{ 
                    width: '100%',
                    backgroundColor: !editing ? '#f5f5f5' : '#fff',
                    color: !editing ? '#00000040' : '#000',
                    cursor: !editing ? 'not-allowed' : 'pointer',
                    border: !editing ? '1px solid #d9d9d9' : '1px solid #d9d9d9'
                  }}
                  key={incident?.id + '-' + (incident?.occurredAt?.toMillis() || 'no-date') + '-' + dateInputKey}
                />
                {editing && (
                  <Tooltip title="Set the incident date to today's date">
                    <button
                      type="button"
                      className="btn btn-link btn-sm mt-1"
                      onClick={handleSetToday}
                      style={{ fontSize: '12px', padding: '2px 4px' }}
                    >
                      📅 Set to Today
                    </button>
                  </Tooltip>
                )}
              </div>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Time Incident Occurred</span>}
              name="occurredTime"
              style={{ marginBottom: '4px' }}
              rules={[
                {
                  pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
                  message: 'Please enter time in HH:mm format (e.g., 14:30)'
                }
              ]}
              extra={editing ? <small style={{ color: '#666' }}>💡 Tip: Type 1430 or 14:30 or just 14 (becomes 14:00)</small> : null}
            >
              <TimeInput
                disabled={!editing}
                value={form.getFieldValue('occurredTime')}
                onChange={(value) => form.setFieldValue('occurredTime', value)}
                showNowButton={editing}
              />
            </Form.Item>
          </Col>

          {/* Initial Summary Field */}
          <Col span={24}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Initial Summary</span>}
              name="summary"
              style={{ marginBottom: '8px' }}
            >
              {editing ? (
                <Input.TextArea
                  rows={3}
                  placeholder="Initial situation description"
                  style={{ width: '100%' }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '6px',
                  border: '1px solid #d9d9d9',
                  minHeight: '32px',
                  color: '#00000040'
                }}>
                  {incident?.summary || 'No initial summary provided'}
                </div>
              )}
            </Form.Item>
          </Col>

          <Col span={24}>
            <Row justify="space-between" style={{ marginBottom: 0, marginTop: '4px' }}>
              <Col>
                <Text type="secondary">
                  Created by: {users?.find(u => u.uid === incident?.createdBy) ? getUserDisplayName(users.find(u => u.uid === incident?.createdBy)) : (incident?.createdByName || 'Unknown User')}
                </Text>
              </Col>
              <Col>
                <Text type="secondary">
                  Created at: {incident?.createdAt?.toDate().toLocaleString()}
                </Text>
              </Col>
              <Col>
                <Text type="secondary" style={{ marginBottom: 0 }}>
                  Last updated: {incident?.updatedAt?.toDate().toLocaleString()}
                </Text>
              </Col>
            </Row>
          </Col>
        </Row>
          </Panel>
        </Collapse>
      </Form>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Delete Incident</span>
          </div>
        }
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeleteConfirmation('');
        }}
        okText="Delete Incident"
        okButtonProps={{ 
          danger: true,
          disabled: deleteConfirmation !== 'DELETE'
        }}
        cancelText="Cancel"
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Are you sure you want to delete <strong>"{incident?.title}"</strong>?</p>
          <div style={{ 
            backgroundColor: '#fff2f0', 
            border: '1px solid #ffccc7', 
            borderRadius: '6px', 
            padding: '12px', 
            marginBottom: '16px' 
          }}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              <strong>Important:</strong> This incident will be removed from the incidents list but will <strong>remain in auditable logs</strong> for compliance purposes. This action cannot be undone.
            </p>
          </div>
          <p><strong>Crisis Type:</strong> {incident?.crisisType}</p>
          <p><strong>Status:</strong> {incident?.status}</p>
          <p><strong>Created:</strong> {incident?.createdAt?.toDate ? incident.createdAt.toDate().toLocaleString() : 'Unknown'}</p>
        </div>
        <div>
          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Type "DELETE" to confirm:</p>
          <Input
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            placeholder="DELETE"
          />
        </div>
      </Modal>

      <GeographySelectionModal
        visible={geographyModalVisible}
        onCancel={() => setGeographyModalVisible(false)}
        onConfirm={handleGeographyConfirm}
        orgId={currentUser?.orgId}
        title="Select Regions for Site Status Export"
      />
    </Card>
  );
};

export default function IncidentDetails() {
  const { id } = useParams();
  const { currentUser, setCurrentUser, loading } = useAuth();
  const { db } = useFirestore();
  const { clearTimelineData } = useTimeline();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [incident, setIncident] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkStatus, setNetworkStatus] = useState({
    type: 'unknown',
    lastChecked: null,
    retryAfter: null
  });
  const [metrics, setMetrics] = useState({
    loadTime: 0,
    queryTime: 0,
    renderTime: 0,
    errorCount: 0,
    retryCount: 0,
    networkLatency: 0,
    dataSize: 0,
    cacheHits: 0,
    cacheMisses: 0
  });
  const [hasError, setHasError] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);
  const [users, setUsers] = useState([]);

  // Refs for tracking previous values to reduce console spam
  const prevUserCountRef = useRef(0);
  const prevMilestoneCountRef = useRef(0);

  const trackPerformance = (metric, value) => {
    setMetrics(prev => ({
      ...prev,
      [metric]: value
    }));
  };

  const measureNetworkLatency = async () => {
    const start = performance.now();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/api/health', {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const latency = performance.now() - start;
        trackPerformance('networkLatency', latency);
        return latency;
      }
    } catch (err) {
      console.error('Error measuring network latency:', err);
    }
    return null;
  };

  const calculateDataSize = (data) => {
    try {
      const jsonString = JSON.stringify(data);
      return new Blob([jsonString]).size;
    } catch (err) {
      console.error('Error calculating data size:', err);
      return 0;
    }
  };

  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return '';
    return input
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .trim();
  };

  const validateUserAccess = (incidentData) => {
    // Check if user has access to this incident
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    if (!incidentData?.orgId) {
      throw new Error('Invalid incident data');
    }

    // Allow super admin access
    if (currentUser.isSuperAdmin) {
      return true;
    }

    // Check organisation access
    if (incidentData.orgId !== currentUser.orgId) {
      throw new Error('You do not have access to this incident');
    }

    return true;
  };

  const validateTimestamp = (timestamp) => {
    if (!timestamp) {
      throw new Error('Invalid timestamp');
    }
    
    // Handle different timestamp formats
    if (timestamp instanceof Timestamp) {
      return true; // Firestore Timestamp
    }
    
    if (timestamp instanceof Date) {
      return true; // JavaScript Date
    }
    
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        throw new Error('Invalid timestamp');
      }
      return true; // Valid ISO string
    }
    
    // Handle Firestore Timestamp-like objects (serialized)
    if (timestamp && (timestamp.seconds !== undefined || timestamp._seconds !== undefined)) {
      return true; // Firestore Timestamp object
    }
    
    throw new Error('Invalid timestamp');
  };

  const validateString = (str, fieldName) => {
    if (typeof str !== 'string' || !str.trim()) {
      throw new Error(`Invalid ${fieldName}`);
    }
    return true;
  };

  const validateEnum = (value, fieldName, allowedValues) => {
    if (!value) {
      throw new Error(`Invalid ${fieldName}: value is required`);
    }
    
    // Special handling for crisis type to handle different formats
    if (fieldName === 'crisisType') {
      const normalizedValue = value.toLowerCase().replace(/[\/\s]+/g, ' ').trim();
      const normalizedAllowedValues = allowedValues.map(v => v.toLowerCase().replace(/[\/\s]+/g, ' ').trim());
      if (!normalizedAllowedValues.includes(normalizedValue)) {
        throw new Error(`Invalid ${fieldName}: ${value}. Allowed values: ${allowedValues.join(', ')}`);
      }
      return true;
    }
    
    // Default validation for other fields
    const normalizedValue = value.toLowerCase();
    const normalizedAllowedValues = allowedValues.map(v => v.toLowerCase());
    if (!normalizedAllowedValues.includes(normalizedValue)) {
      throw new Error(`Invalid ${fieldName}: ${value}. Allowed values: ${allowedValues.join(', ')}`);
    }
    return true;
  };

  const validateIncidentData = (data) => {
    if (!data) {
      throw new Error('No incident data provided');
    }

    // Validate required fields
    validateString(data.title, 'title');
    validateString(data.orgId, 'orgId');
    validateString(data.status, 'status');
    validateString(data.severity, 'severity');
    validateString(data.crisisType, 'crisisType');

    // Validate enums
    validateEnum(data.status, 'status', INCIDENT_STATUS);
    validateEnum(data.severity, 'severity', SEVERITY_LEVELS);
    validateEnum(data.crisisType, 'crisisType', INCIDENT_TYPES);

    // If crisis type is "Other", validate that customCrisisType is provided (for new data)
    // Skip validation for existing data that might not have been migrated yet
    if (data.crisisType === 'Other' && data.customCrisisType !== undefined && (!data.customCrisisType || !data.customCrisisType.trim())) {
      console.warn('Crisis type is "Other" but customCrisisType is empty - this may be legacy data');
    }

    // Validate timestamps
    validateTimestamp(data.createdAt);
    if (data.updatedAt) {
      validateTimestamp(data.updatedAt);
    }

    // Sanitize text fields but preserve original capitalization
    data.title = sanitizeInput(data.title); // Don't modify capitalization
    data.description = sanitizeInput(data.description);
    data.status = sanitizeInput(data.status).toLowerCase(); // Normalize status to lowercase
    data.severity = sanitizeInput(data.severity).toLowerCase(); // Normalize severity to lowercase
    data.crisisType = sanitizeInput(data.crisisType); // Don't lowercase crisis type as it's case-sensitive
    if (data.customCrisisType) {
      data.customCrisisType = sanitizeInput(data.customCrisisType); // Sanitize custom crisis type
    }

    return true;
  };

  const validateMilestoneData = (data) => {
    if (!data) {
      throw new Error('No milestone data provided');
    }

    // Validate required fields
    validateString(data.title, 'title');
    validateString(data.orgId, 'orgId');
    validateString(data.incidentId, 'incidentId');
    validateString(data.status, 'status');

    // Validate enums - Updated to match server-side normalized status values
    validateEnum(data.status, 'status', ['Not Started', 'In Progress', 'Complete', 'Blocked']);

    // Validate timestamps
    validateTimestamp(data.dueDate);
    if (data.completedAt) {
      validateTimestamp(data.completedAt);
    }

    // Sanitize text fields
    data.title = sanitizeInput(data.title);
    data.description = sanitizeInput(data.description);
    data.status = sanitizeInput(data.status);

    return true;
  };

  const validateUserData = (data) => {
    if (!data) {
      throw new Error('No user data provided');
    }

    // Validate required fields
    validateString(data.orgId, 'orgId');
    validateString(data.role, 'role');

    // Validate enums
    validateEnum(data.role, 'role', ['admin', 'user', 'viewer', 'developer']);

    return true;
  };

  const handleNetworkError = (err) => {
    if (!navigator.onLine) {
      setError('You are offline. Please check your internet connection.');
      return true;
    }

    if (err.code === 'unavailable' || err.code === 'failed-precondition') {
      setNetworkStatus(prev => ({
        ...prev,
        type: 'error',
        lastChecked: new Date(),
        retryAfter: new Date(Date.now() + 5000) // Retry after 5 seconds
      }));
      setError('Network error. Please check your connection and try again.');
      return true;
    }

    if (err.code === 'resource-exhausted') {
      setNetworkStatus(prev => ({
        ...prev,
        type: 'rate-limited',
        lastChecked: new Date(),
        retryAfter: new Date(Date.now() + 60000) // Retry after 1 minute
      }));
      setError('Too many requests. Please try again later.');
      return true;
    }

    return false;
  };

  const migrateIncidentData = (data) => {
    // Handle old data structure where custom crisis types were stored directly in crisisType
    const allowedTypes = ['Active Threat', 'Aggressive Customer', 'Critical Incident', 'Cyber Incident', 
                         'Cyclone / Severe Weather', 'Earthquake', 'Exercise', 'Extortion', 'Fire', 'Flood', 
                         'Kidnap for Ransom (KRE)', 'Medical Emergency', 'Meeting/Event Incident', 
                         'Protest / Civil Unrest', 'Reputational Incident', 'Suspicious Item / Bomb Threat', 
                         'Threats Against Executive', 'Other'];
    
    // If crisisType is not in the allowed list, it's probably a custom type that should be migrated
    if (data.crisisType && !allowedTypes.includes(data.crisisType)) {
      console.log(`DEBUG - Migrating custom crisis type: "${data.crisisType}" -> "Other" with customCrisisType`);
      return {
        ...data,
        customCrisisType: data.crisisType, // Move the custom type to the new field
        crisisType: 'Other' // Set the base type to "Other"
      };
    }
    
    // If it's already "Other" but missing customCrisisType, that's okay for existing data
    return data;
  };

  const loadData = async () => {
    const startTime = performance.now();
    setError('');

    try {
      // Debug authentication state
      console.log('DEBUG - Auth State:', {
        isAuthenticated: !!currentUser,
        auth: currentUser ? {
          uid: currentUser.uid,
          email: currentUser.email,
          emailVerified: currentUser.emailVerified,
          isAnonymous: currentUser.isAnonymous,
          metadata: currentUser.metadata,
          providerData: currentUser.providerData,
          refreshToken: currentUser.refreshToken,
          tenantId: currentUser.tenantId
        } : null
      });

      // Check if user is authenticated and has required data
      if (!currentUser?.uid) {
        throw new Error('User not authenticated');
      }

      if (!currentUser?.orgId) {
        throw new Error('User organisation ID not found');
      }

      // Debug user data
      console.log('DEBUG - User Data:', {
        uid: currentUser.uid,
        email: currentUser.email,
        orgId: currentUser.orgId,
        role: currentUser.role,
        isAdmin: currentUser.role === 'admin',
        isDeveloper: currentUser.role === 'developer',
        isLoaded: currentUser.isLoaded,
        customClaims: currentUser.customClaims,
        displayName: currentUser.displayName,
        photoURL: currentUser.photoURL
      });

      // Set up real-time listener for incident data
      const incidentRef = doc(db, 'c3_incidents', id);
      console.log('DEBUG - Setting up incident listener:', {
        id,
        path: `c3_incidents/${id}`,
        userOrgId: currentUser.orgId
      });
      
      const incidentUnsubscribe = onSnapshot(incidentRef, 
        (incidentSnap) => {
          // Only log incident updates if there's an actual change in key fields
          const previousTitle = incident?.title;
          const previousStatus = incident?.status;
          const currentData = incidentSnap.exists() ? incidentSnap.data() : null;
          const hasSignificantChange = currentData && (
            currentData.title !== previousTitle ||
            currentData.status !== previousStatus
          );
          
          if (hasSignificantChange || !incidentSnap.exists()) {
            console.log('DEBUG - Incident snapshot update:', {
              exists: incidentSnap.exists(),
              id: incidentSnap.id,
              changes: hasSignificantChange ? { 
                title: `${previousTitle} -> ${currentData.title}`,
                status: `${previousStatus} -> ${currentData.status}`
              } : 'none'
            });
          }

          if (!incidentSnap.exists()) {
            setError('Incident not found');
            return;
          }

          let incidentData = incidentSnap.data();
          // Reduced logging - only on significant changes
          if (hasSignificantChange) {
            console.log('DEBUG - Raw incident data from listener:', {
              id: incidentSnap.id,
              title: incidentData.title,
              status: incidentData.status,
              createdBy: incidentData.createdBy,
              updatedAt: incidentData.updatedAt?.toDate(),
              createdAt: incidentData.createdAt?.toDate()
            });
          }

          // Migrate old data structure if needed
          incidentData = migrateIncidentData(incidentData);

          // 🔥 EARLY EXIT: Redirect user if incident is soft-deleted to prevent validation errors
          if (incidentData.status === 'deleted') {
            console.log('🔄 Incident soft-deleted, redirecting to incidents list...');
            navigate('/incidents');
            return;
          }

          try {
            validateIncidentData(incidentData);
            validateUserAccess(incidentData);
            
            // 🔧 CRITICAL DEBUG: Track real-time updates
            console.log('🔧 REAL-TIME LISTENER: About to update incident state:', {
              incidentId: incidentSnap.id,
              newStatus: incidentData.status,
              timestamp: new Date().toISOString()
            });
            
            setIncident({ id: incidentSnap.id, ...incidentData });
            console.log('✅ Incident data updated in real-time');
          } catch (error) {
            console.error('❌ Error validating incident data:', error);
            setError(error.message);
          }
        },
        (error) => {
          console.error('❌ Error in incident listener:', error);
          setError('Failed to load incident data: ' + error.message);
        }
      );

      // Fetch users for dropdowns - include both registered users and CMT members (this can stay as one-time fetch)
      const usersRef = collection(db, 'c3_users');
      const usersQuery = query(
        usersRef,
        where('orgId', '==', currentUser.orgId)
      );
      const usersSnap = await getDocs(usersQuery);
      const existingUsers = usersSnap.docs.map(doc => ({
        uid: doc.id,
        source: 'c3_users',
        ...doc.data()
      }));

      // Fetch organization document to get CMT members
      const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
      const orgSnapshot = await getDoc(orgRef);
      let cmtMembers = [];
      
      if (orgSnapshot.exists()) {
        const orgData = orgSnapshot.data();
        cmtMembers = (orgData.cmtMembers || []).map((member, index) => ({
          uid: `cmt-${index}-${member.email}`, // Create a unique ID for CMT members
          source: 'cmt_members',
          email: member.email,
          fullName: member.fullName,
          firstName: member.fullName?.split(' ')[0] || '',
          lastName: member.fullName?.split(' ').slice(1).join(' ') || '',
          phone: member.phone,
          cmtRole: member.cmtRole,
          role: 'cmt_member', // Default role for CMT members
          hasBackup: member.hasBackup,
          backupName: member.backupName,
          backupPhone: member.backupPhone,
          lastLogin: null // CMT members from org data won't have login info
        }));
      }
      
      // Combine both lists, avoiding duplicates (prefer c3_users data over CMT data for existing users)
      const existingUserEmails = new Set(existingUsers.map(user => user.email));
      const uniqueCmtMembers = cmtMembers.filter(member => !existingUserEmails.has(member.email));
      
      const usersList = [...existingUsers, ...uniqueCmtMembers];
      
      // Sort by name
      usersList.sort((a, b) => {
        const nameA = a.fullName || a.firstName || a.email || '';
        const nameB = b.fullName || b.firstName || b.email || '';
        return nameA.localeCompare(nameB);
      });

      // Only log user fetches when count changes significantly
      const prevUserCount = prevUserCountRef.current;
      const userCountChanged = Math.abs(usersList.length - prevUserCount) > 0;
      prevUserCountRef.current = usersList.length;
      
      if (userCountChanged) {
        console.log('DEBUG - Users fetched (count changed):', {
          previousCount: prevUserCount,
          currentCount: usersList.length,
          change: usersList.length - prevUserCount
        });
      }
      setUsers(usersList);

      // Set up optimized milestone query with orgId filter for security
      const milestonesRef = collection(db, 'c3_milestones');
      const q = query(
        milestonesRef,
        where('incidentId', '==', id),
        where('orgId', '==', currentUser.orgId),
        orderBy('dueDate', 'asc'),
        limit(50)
      );

      // Use onSnapshot with error handling and cleanup for milestones
      const milestonesUnsubscribe = onSnapshot(q, 
        (querySnapshot) => {
          const startTime = performance.now();
          try {
            // Only log milestone snapshots when count changes
            const prevMilestoneCount = prevMilestoneCountRef.current;
            const currentCount = querySnapshot.size;
            const countChanged = currentCount !== prevMilestoneCount;
            prevMilestoneCountRef.current = currentCount;
            
            if (countChanged || querySnapshot.empty) {
              console.log('DEBUG - Milestone snapshot (count changed):', {
                previousCount: prevMilestoneCount,
                currentCount: currentCount,
                empty: querySnapshot.empty
              });
            }

            const milestoneList = querySnapshot.docs
              .map(doc => {
                const data = doc.data();
                try {
                  validateMilestoneData(data);
                  return {
                    id: doc.id,
                    ...data,
                    dueDate: data.dueDate
                  };
                } catch (err) {
                  console.error('Invalid milestone data:', err);
                  return null;
                }
              })
              .filter(Boolean);

            // Only log processed milestones on count change
            if (countChanged) {
              console.log('DEBUG - Processed milestones (count changed):', milestoneList.length, 'milestones');
            }

            const milestoneDataSize = calculateDataSize(milestoneList);
            trackPerformance('dataSize', metrics.dataSize + milestoneDataSize);

            setMilestones(milestoneList);
            setError('');
            
            const renderTime = performance.now() - startTime;
            trackPerformance('renderTime', renderTime);
          } catch (err) {
            console.error('Error processing milestones:', err);
            setError('Error processing milestone data');
            trackPerformance('errorCount', metrics.errorCount + 1);
          }
        },
        (err) => {
          console.error('Error in milestone listener:', err);
          setError('Failed to load milestone data');
          trackPerformance('errorCount', metrics.errorCount + 1);
        }
      );

      const loadTime = performance.now() - startTime;
      trackPerformance('loadTime', loadTime);

      return { incidentUnsubscribe, milestonesUnsubscribe };

    } catch (err) {
      console.error('Error loading data:', err, {
        code: err.code,
        message: err.message,
        stack: err.stack,
        currentUser: currentUser ? {
          uid: currentUser.uid,
          orgId: currentUser.orgId,
          role: currentUser.role
        } : null
      });

      if (!handleNetworkError(err)) {
        if (err.message === 'User not authenticated') {
          setError('Please log in to view this incident');
        } else if (err.message === 'User organisation ID not found') {
          setError('Your account is not properly configured. Please contact support.');
        } else if (err.message === 'You do not have access to this incident') {
          setError('You do not have permission to view this incident');
        } else {
          setError('Failed to load data. Please try refreshing the page.');
        }
        trackPerformance('errorCount', metrics.errorCount + 1);
      }

      // Return empty cleanup functions to prevent unsubscribe errors
      return {
        incidentUnsubscribe: () => {},
        milestonesUnsubscribe: () => {}
      };
    } finally {
      setError('');
    }
  };

  useEffect(() => {
    console.log('Effect triggered with:', {
      id,
      currentUser: currentUser ? {
        uid: currentUser.uid,
        email: currentUser.email,
        orgId: currentUser.orgId,
        role: currentUser.role,
        isLoaded: currentUser.isLoaded,
        loading: loading
      } : null,
      loading
    });

    if (!id) {
      console.log('No incident ID provided');
      return;
    }

    // Check if we're still loading auth state
    if (loading) {
      console.log('Auth state is still loading...');
      return;
    }

    // Check if user data is fully loaded
    if (!currentUser?.isLoaded) {
      console.log('User data not fully loaded:', {
        currentUser: currentUser ? {
          uid: currentUser.uid,
          email: currentUser.email,
          orgId: currentUser.orgId,
          role: currentUser.role,
          isLoaded: currentUser.isLoaded
        } : null
      });
      return;
    }

    let unsubscribe = null;
    let isMounted = true;

    const handleOnline = () => {
      setIsOnline(true);
      setNetworkStatus(prev => ({
        ...prev,
        type: 'online',
        lastChecked: new Date()
      }));
      if (error?.includes('network')) {
        handleRefresh();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setNetworkStatus(prev => ({
        ...prev,
        type: 'offline',
        lastChecked: new Date()
      }));
      setError('You are offline. Please check your internet connection.');
    };

    const checkNetwork = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch('/api/health', {
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          handleOnline();
        } else {
          handleOffline();
        }
      } catch (err) {
        handleOffline();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check network status periodically
    const intervalId = setInterval(checkNetwork, 30000);

    // Initial data load
    loadData()
      .then(unsubscribeFunctions => {
        if (isMounted) {
          unsubscribe = unsubscribeFunctions;
        }
      })
      .catch(error => {
        console.error('Error in loadData:', error);
        // Don't set unsubscribe if loadData failed
        unsubscribe = null;
      });

    // SURGICAL ADDITION: Start AGGRESSIVE video meeting monitoring
    // Only runs when viewing incident details - zero impact on other pages
    console.log('🚨 Starting AGGRESSIVE video meeting monitoring for incident details page');
            videoMeetingMonitorService.initialize();
        videoMeetingMonitorService.startAggressiveMode();
    
    // Debug: Log service status every 30 seconds
    let debugInterval = null;
    if (isMounted) {
      debugInterval = setInterval(() => {
        const status = videoMeetingMonitorService.getStatus();
        console.log('🔍 Video Monitor Status:', status);
      }, 30000);
    }

    return () => {
      isMounted = false;
      if (unsubscribe) {
        // Unsubscribe from both incident and milestones listeners
        try {
          if (typeof unsubscribe === 'function') {
            // Handle old format where it was just a function
            unsubscribe();
          } else if (typeof unsubscribe === 'object' && unsubscribe !== null) {
            // Handle new format with multiple unsubscribe functions
            if (unsubscribe.incidentUnsubscribe && typeof unsubscribe.incidentUnsubscribe === 'function') {
              unsubscribe.incidentUnsubscribe();
            }
            if (unsubscribe.milestonesUnsubscribe && typeof unsubscribe.milestonesUnsubscribe === 'function') {
              unsubscribe.milestonesUnsubscribe();
            }
          }
        } catch (error) {
          console.error('Error during cleanup:', error);
        }
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
      
      // SURGICAL CLEANUP: Stop AGGRESSIVE video meeting monitoring when leaving incident details
      console.log('🚨 Stopping AGGRESSIVE video meeting monitoring - leaving incident details page');
      if (debugInterval) clearInterval(debugInterval);
      videoMeetingMonitorService.stop();
      
      // TEMPORARILY DISABLED - Clear timeline data for this incident when leaving the page
      if (id) {
        clearTimelineData(id);
      }
    };
      }, [id, currentUser?.isLoaded, loading, clearTimelineData]);

  // Add debug logging for milestones
  useEffect(() => {
    console.log('Milestones state updated:', {
      count: milestones.length,
      milestones: milestones.map(m => ({
        id: m.id,
        title: m.title,
        dueDate: m.dueDate?.toDate(),
        status: m.status
      }))
    });
  }, [milestones]);



  const handleMilestoneClick = (milestone) => {
    setSelectedMilestone(milestone);
    setIsModalOpen(true);
  };

  const handleMilestoneDrag = async (milestoneId, newDate) => {
    try {
      const milestoneRef = doc(db, 'c3_milestones', milestoneId);
      await updateDoc(milestoneRef, {
        dueDate: newDate,
        updatedAt: new Date()
      });
    } catch (err) {
      console.error('Error updating milestone date:', err);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedMilestone(null);
  };

  const handleRetry = () => {
    if (retryCount < MAX_RETRIES) {
      setRetryCount(prev => prev + 1);
      trackPerformance('retryCount', metrics.retryCount + 1);
      setError('');
      loadData();
    } else {
      setError('Maximum retry attempts reached. Please try refreshing the page.');
    }
  };

  const handleRefresh = async () => {
    try {
      // Refresh user data
      const userRef = doc(db, 'c3_users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setCurrentUser(prev => ({ ...prev, ...userData }));
      }

      // Reset retry count and reload
      setRetryCount(0);
      setError('');
      loadData();
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Failed to refresh data. Please try again.');
    }
  };

  const handleError = (error, errorInfo) => {
    console.error('Error in IncidentDetails:', error, errorInfo);
    setHasError(true);
    setErrorInfo(errorInfo);
    trackPerformance('errorCount', metrics.errorCount + 1);
  };

  const LoadingSkeleton = () => (
    <div className="p-4" role="main">
      <PageHeader title="Crisis Details" incident={incident} />
      <div className="card mb-4">
        <div className="card-body">
          <div className="placeholder-glow" aria-hidden="true">
            <h2 className="card-title h4 placeholder col-6"></h2>
            <div className="mb-3">
              <span className="badge bg-primary me-2 placeholder col-2"></span>
              <span className="badge bg-danger me-2 placeholder col-2"></span>
              <span className="badge bg-info placeholder col-2"></span>
            </div>
            <p className="card-text placeholder-glow">
              <span className="placeholder col-12"></span>
              <span className="placeholder col-12"></span>
              <span className="placeholder col-8"></span>
            </p>
            <div className="text-muted">
              <small className="placeholder col-4"></small>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title h5 mb-0 placeholder col-4"></h3>
        </div>
        <div className="card-body">
          <div className="list-group">
            {[1, 2, 3].map((i) => (
              <div key={i} className="list-group-item">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="h6 mb-0 placeholder col-4"></h4>
                  <span className="badge bg-primary placeholder col-2"></span>
                </div>
                <p className="mb-1 placeholder-glow">
                  <span className="placeholder col-12"></span>
                  <span className="placeholder col-8"></span>
                </p>
                <small className="text-muted placeholder col-3"></small>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const handleIncidentUpdate = async (updates) => {
    try {
      const incidentRef = doc(db, 'c3_incidents', id);
      await updateDoc(incidentRef, updates);
      
      // Update local state with the new values - create a new object to ensure state change detection
      setIncident(prevIncident => {
        const updatedIncident = {
          ...prevIncident,
          ...updates
        };
        
        // Debug logging to track state updates
        console.log('Incident state updated:', {
          oldOccurredAt: prevIncident?.occurredAt ? moment(prevIncident.occurredAt.toDate()).format('DD/MM/YYYY HH:mm') : 'Not set',
          newOccurredAt: updatedIncident?.occurredAt ? moment(updatedIncident.occurredAt.toDate()).format('DD/MM/YYYY HH:mm') : 'Not set'
        });
        
        return updatedIncident;
      });
      
      message.success('Incident updated successfully');
    } catch (error) {
      console.error('Error updating incident:', error);
      setError('Failed to update incident');
    }
  };

  const handleDeleteIncident = async () => {
    try {
      const incidentRef = doc(db, 'c3_incidents', id);
      await updateDoc(incidentRef, {
        status: 'deleted',
        deletedAt: new Date(),
        deletedBy: currentUser.uid
      });

      message.success(`Incident "${incident?.title}" has been deleted. It will no longer appear in the incidents list but remains in audit logs.`);
      navigate('/incidents');
    } catch (error) {
      console.error('Error deleting incident:', error);
      message.error('Failed to delete incident');
    }
  };





  if (hasError) {
    return (
      <div className="p-4" role="main">
        <PageHeader title="Crisis Details" incident={incident} />
        <div 
          className="alert alert-danger" 
          role="alert"
          aria-live="polite"
          aria-atomic="true"
        >
          <h4 className="alert-heading">Something went wrong</h4>
          <p>An unexpected error occurred while loading the crisis details.</p>
          <hr />
          <div className="d-flex gap-2" role="group" aria-label="Error recovery actions">
            <button 
              className="btn btn-outline-danger btn-sm"
              onClick={() => {
                setHasError(false);
                setErrorInfo(null);
                loadData();
              }}
              aria-label="Retry loading incident"
            >
              Retry
            </button>
            <button 
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate('/incidents')}
              aria-label="Return to incidents list"
            >
              Back to Incidents
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && errorInfo && (
            <div className="mt-3">
              <details>
                <summary>Error Details</summary>
                <pre className="mt-2 p-2 bg-light">
                  {errorInfo.componentStack}
                </pre>
              </details>
            </div>
          )}
        </div>
      </div>
    );
  }

  try {
    if (error) {
      return (
        <div className="p-4" role="main">
          <PageHeader title="Crisis Details" incident={incident} />
          <div 
            className="alert alert-danger" 
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            <h4 className="alert-heading" id="error-heading">Error Loading Incident</h4>
            <p id="error-message">{error}</p>
            <hr />
            <div className="d-flex gap-2" role="group" aria-label="Error actions">
              <button 
                className="btn btn-outline-danger btn-sm"
                onClick={handleRetry}
                disabled={retryCount >= MAX_RETRIES}
                aria-label={`Retry loading incident. ${MAX_RETRIES - retryCount} attempts remaining.`}
              >
                {retryCount < MAX_RETRIES ? `Retry (${MAX_RETRIES - retryCount} left)` : 'Max Retries Reached'}
              </button>
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={handleRefresh}
                aria-label="Refresh incident data"
              >
                Refresh Data
              </button>
              <button 
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate('/incidents')}
                aria-label="Return to incidents list"
              >
                Back to Incidents
              </button>
            </div>
            <hr />
            <div 
              className="text-muted"
              role="complementary"
              aria-label="Performance metrics"
            >
              <h5 className="h6">Performance Metrics:</h5>
              <ul className="list-unstyled">
                <li>Load Time: {metrics.loadTime.toFixed(2)}ms</li>
                <li>Query Time: {metrics.queryTime.toFixed(2)}ms</li>
                <li>Render Time: {metrics.renderTime.toFixed(2)}ms</li>
                <li>Network Latency: {metrics.networkLatency.toFixed(2)}ms</li>
                <li>Data Size: {(metrics.dataSize / 1024).toFixed(2)}KB</li>
                <li>Cache Hits: {metrics.cacheHits}</li>
                <li>Cache Misses: {metrics.cacheMisses}</li>
                <li>Errors: {metrics.errorCount}</li>
                <li>Retries: {metrics.retryCount}</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    if (!isOnline) {
      return (
        <div className="p-4" role="main">
          <PageHeader title="Crisis Details" incident={incident} />
          <div 
            className="alert alert-warning" 
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            <h4 className="alert-heading" id="offline-heading">You are offline</h4>
            <p id="offline-message">Please check your internet connection and try again.</p>
            <hr />
            <div className="d-flex gap-2" role="group" aria-label="Offline actions">
              <button 
                className="btn btn-outline-warning btn-sm"
                onClick={handleRefresh}
                aria-label="Retry loading incident"
              >
                Retry
              </button>
              <button 
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate('/incidents')}
                aria-label="Return to incidents list"
              >
                Back to Incidents
              </button>
            </div>
            <hr />
            <div className="text-muted">
              <small>
                Last checked: {networkStatus.lastChecked?.toLocaleString() || 'Never'}
              </small>
            </div>
          </div>
        </div>
      );
    }

    if (loading) {
      return <LoadingSkeleton />;
    }

    return (
      <div className="p-4">
        <PageHeader title="Crisis Details" incident={incident} />
        <div className="incident-details">
          <div className="main-content" style={{ padding: '0 16px 0 16px' }}>
            {error ? (
              <div className="error-container">
                <div className="error-message">{error}</div>
                <button onClick={handleRetry} className="retry-button">
                  Retry
                </button>
              </div>
            ) : (
              <>
                <IncidentHeader
                  incident={incident}
                  onUpdate={handleIncidentUpdate}
                  users={users}
                  onDelete={handleDeleteIncident}
                />



                {/* TEMPORARILY DISABLED TO STOP INFINITE LOOP */}
                {/* <SmartAutomationAlerts incidentId={id} incidentData={incident} /> */}

                {/* TEMPORARILY DISABLED TO STOP INFINITE LOOP */}
                {/* <Phase2AutomationPanel incidentId={id} incidentData={incident} /> */}

                <div style={{ marginTop: '16px', marginBottom: 0 }}>
                  <IncidentDetailsTabs incidentId={id} incident={incident} />
                </div>
              </>
            )}
          </div>

          <div className="chat-sidebar">
            <LiveChatPanel incidentId={id} />
          </div>
        </div>
      </div>
    );
  } catch (err) {
    handleError(err, {
      componentStack: err.stack
    });
    return null;
  }
}

function getStatusBadgeClass(status) {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'bg-danger';
    case 'stabilising':
      return 'bg-warning';
    case 'resolved':
      return 'bg-success';
    case 'exercise':
      return 'bg-info';
    default:
      return 'bg-secondary';
  }
}

function getSeverityBadgeClass(severity) {
  switch (severity?.toLowerCase()) {
    case 'extreme':
      return 'bg-danger';
    case 'high':
      return 'bg-warning';
    case 'moderate':
      return 'bg-info';
    case 'low':
      return 'bg-success';
    default:
      return 'bg-secondary';
  }
} 